/**
 * Übersicht des Objekts — die Antwort auf „Was ist jetzt wichtig?" (§4): wenige Kennzahlen, die
 * Apartments und was Gäste dieses Objekts sehen und bekommen.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Button, EmptyState } from '@/shared/components';
import { I } from '@/shared/icons';
import { useApi, type PropertyGroupResponse } from '@/shared/api';
import { useAuth } from '@/shared/auth';
import { ObjectApartmentTable } from '../ObjectApartmentTable';
import { ObjectFactsBand } from './ObjectFactsBand';
import { ObjectGuestReadiness } from './ObjectGuestReadiness';
import { useObjectDetail } from './ObjectDetailContext';
import { visibleObjectAreas } from './objectAreas';
import { guestItems } from './objectGuestItems';
import { overviewFacts } from './objectOverviewModel';
import { useObjectStatusData } from './useObjectStatusData';

export function ObjectOverviewTab() {
  const { groupId } = useObjectDetail();
  const api = useApi();
  const { data: group } = useQuery({
    queryKey: ['property-groups', groupId],
    queryFn: () => api.propertyGroups.get(groupId),
  });
  if (!group) return null;
  return <ObjectOverview group={group} />;
}

function ObjectOverview({ group }: { group: PropertyGroupResponse }) {
  const { canEdit } = useObjectDetail();
  const api = useApi();
  const navigate = useNavigate();
  const areas = visibleObjectAreas(useAuth().user, canEdit);
  const status = useObjectStatusData(group, areas);

  const apartmentIds = useMemo(() => group.apartments.map((a) => a.id), [group]);
  const { data: apartments } = useQuery({
    queryKey: ['properties', 'for-object', group.id, apartmentIds],
    queryFn: () => api.properties.list({ pageSize: 100, propertyIds: apartmentIds }),
    enabled: apartmentIds.length > 0,
  });
  const detailById = useMemo(
    () => new Map((apartments?.items ?? []).map((p) => [p.id, p])),
    [apartments],
  );

  return (
    <div className="@container space-y-5">
      <ObjectFactsBand facts={overviewFacts(status, areas)} />
      <div className="@3xl:grid-cols-[minmax(0,1fr)_320px] grid grid-cols-1 items-start gap-5">
        <section aria-labelledby="object-apartments" className="@container min-w-0 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h2 id="object-apartments" className="text-ink font-serif text-xl">
              Apartments
            </h2>
            {canEdit && (
              <Button
                variant="brand"
                size="sm"
                className="min-h-11"
                leftIcon={<I name="Plus" size={14} />}
                onClick={() =>
                  void navigate({ to: '/properties/new', search: { objectId: group.id } })
                }
              >
                Apartment hinzufügen
              </Button>
            )}
          </div>
          {group.apartments.length === 0 ? (
            <EmptyState
              icon="Building"
              title="Noch keine Apartments"
              description="Fügen Sie das erste Apartment für dieses Objekt hinzu."
            />
          ) : (
            <ObjectApartmentTable apartments={group.apartments} detailById={detailById} />
          )}
        </section>
        <ObjectGuestReadiness groupId={group.id} items={guestItems(status, areas, canEdit)} />
      </div>
    </div>
  );
}
